import { Stack, useResponsiveValue } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useState } from 'react';
import {
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { SuperPayBankAccountDTO } from 'shared/modules/superpay/SuperPayDTO';
import { useBankAccountVerificationAnalytics } from '../../data/SuperPayAnalytics';
import {
  ICantFindMvCodeLink,
  InstantBankVerificationFormContent,
  useInstantBankVerificationStep,
} from './InstantBankVerificationForm';
import { UploadScreenshotStep } from './UploadScreenshotStep';

type Step = 'code' | 'upload_screenshot';

interface BankVerificationContentDrawerProps {
  bankAccountDetails?: SuperPayBankAccountDTO;
  onClose?: () => void;
  onComplete: () => void;
}

export function BankVerificationContentDrawer({
  bankAccountDetails,
  onClose,
  onComplete,
}: BankVerificationContentDrawerProps) {
  const [step, setStep] = useState<Step>('code');
  const { trackVerified } = useBankAccountVerificationAnalytics();

  if (step === 'upload_screenshot') {
    return (
      <Box maxWidth={['initial', 'initial', '432px']}>
        <UploadScreenshotStep
          onBack={() => setStep('code')}
          onClose={onClose}
          onComplete={onComplete}
          onVerified={trackVerified}
        />
      </Box>
    );
  }

  return (
    <CodeStep
      bankAccountDetails={bankAccountDetails}
      onClose={onClose}
      onComplete={onComplete}
      onSwitchToScreenshot={() => setStep('upload_screenshot')}
    />
  );
}

interface CodeStepProps extends BankVerificationContentDrawerProps {
  onSwitchToScreenshot: () => void;
}

function CodeStep({
  bankAccountDetails,
  onClose,
  onComplete,
  onSwitchToScreenshot,
}: CodeStepProps) {
  const isMobile = useResponsiveValue(true, false);
  const { trackVerified, trackCantFindMvCode } =
    useBankAccountVerificationAnalytics();
  const { formik, isLocked, isSubmitDisabled } = useInstantBankVerificationStep(
    {
      bankAccount: bankAccountDetails,
      onComplete: () => {
        trackVerified();
        onComplete();
      },
      onLocked: onClose,
    },
  );

  return (
    <Box maxWidth={['initial', 'initial', '432px']}>
      <FormikDrawerContent
        formik={formik}
        title="Verify Bank Account"
        disableAutoClose={true}
        onClose={onClose}
        disableNavigationBlock={isLocked}
        actions={
          !!bankAccountDetails && (
            <FormikDrawerActions
              submitButtonLabel="Verify"
              submitDisabled={isSubmitDisabled}
              fullWidth={isMobile}
            />
          )
        }
      >
        <Box marginTop="-small">
          <Stack space="small">
            <InstantBankVerificationFormContent
              bankAccount={bankAccountDetails}
            />
            <ICantFindMvCodeLink
              isLocked={isLocked}
              onClick={() => {
                trackCantFindMvCode();
                onSwitchToScreenshot();
              }}
            />
          </Stack>
        </Box>
      </FormikDrawerContent>
    </Box>
  );
}
